/**
 * The scene stack and the frame loop (docs/spec/04 §6): input goes to the top scene, the top scene
 * (and anything it overlays) is drawn into the virtual screen, and the display pass puts that on
 * the window.
 */

import type { Controls } from '../input/controls';
import type { Display } from '../render/display/display';
import { Screen, SCREEN_H, SCREEN_W } from '../render/screen';
import { drawSkyPanels, PANEL_W } from '../render/sidePanels';
import { Starfield } from '../render/starfield';
import type { PointerInput, Route, Scene, SceneFactory, SceneHost, Services } from '../ui/scene';

/** The screen with a panel either side. */
const WIDE_W = SCREEN_W + PANEL_W * 2;

export class App implements SceneHost {
  private stack: { scene: Scene; route: Route }[] = [];
  private last = 0;
  /** Tests: the animation-frame loop stops advancing time; only `frame()` calls move it (window.__game.advance). */
  frozen = false;
  /** Something changed since the last draw (frozen mode draws only then, so screenshots are quick). */
  private dirty = true;
  /** Draw against the top of the window, leaving room below for touch controls. */
  top = false;
  /** The wide layout's canvas and panels (04 §3), made the first time a window is wide enough. */
  private wide: { all: Screen; left: Screen; right: Screen; stars: Starfield } | null = null;
  /** Where the screen starts within what is on the window: 0, or a panel's width. */
  private inset = 0;

  constructor(
    readonly services: Services,
    private readonly factory: SceneFactory,
    private readonly screen: Screen,
    private readonly display: Display,
    private readonly controls: Controls,
  ) {}

  get scene(): Scene | null {
    return this.stack[this.stack.length - 1]?.scene ?? null;
  }

  get route(): Route | null {
    return this.stack[this.stack.length - 1]?.route ?? null;
  }

  go(route: Route): void {
    for (const { scene } of [...this.stack].reverse()) scene.leave?.();
    this.stack = [];
    this.open(route);
  }

  push(route: Route): void {
    this.scene?.leave?.();
    this.open(route);
  }

  back(): void {
    const gone = this.stack.pop();
    gone?.scene.leave?.();
    const top = this.scene;
    if (top) {
      this.controls.reset();
      this.dirty = true;
      top.enter?.();
    } else this.go({ to: 'title', skipPress: true });
  }

  private open(route: Route): void {
    this.dirty = true;
    const scene = this.factory(route, this);
    this.stack.push({ scene, route });
    this.controls.reset();
    scene.enter?.();
  }

  /** A pointer in the pixels of what is on the window (panels and all). */
  pointer(p: PointerInput): void {
    const x = p.x - this.inset;
    if (x < 0 || x >= SCREEN_W) return;
    this.dirty = true;
    this.scene?.pointer?.({ ...p, x });
  }

  /** Room for the panels: a window at least as wide as panel, screen, panel, and no touch controls. */
  private isWide(): boolean {
    if (this.top || !this.services.options.get().sidePanels) return false;
    return window.innerWidth * SCREEN_H >= window.innerHeight * WIDE_W;
  }

  /** Start the loop. */
  run(): void {
    const frame = (now: number): void => {
      const dt = this.last === 0 ? 16 : Math.min(100, now - this.last);
      this.last = now;
      if (!this.frozen) this.frame(dt);
      else if (this.dirty) this.render();
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  frame(dtMs: number): void {
    this.wide?.stars.update(dtMs, 0.5);
    this.controls.update(dtMs);
    this.scene?.update(dtMs);
    this.dirty = true;
    this.render();
  }

  render(): void {
    this.dirty = false;
    if (this.stack.length === 0) return;
    let base = this.stack.length - 1;
    while (base > 0 && this.stack[base]?.scene.overlay === true) base--;
    for (let i = base; i < this.stack.length; i++) this.stack[i]?.scene.draw(this.screen);
    const o = this.services.options.get();
    this.display.resize();
    if (!this.isWide()) {
      this.inset = 0;
      this.display.present(this.screen.canvas, o.crt, o.scale, this.top);
      return;
    }
    const w = (this.wide ??= {
      all: new Screen(WIDE_W, SCREEN_H),
      left: new Screen(PANEL_W, SCREEN_H),
      right: new Screen(PANEL_W, SCREEN_H),
      stars: new Starfield(23),
    });
    // The panels belong to the scene the screen is showing (the game under a pause menu, say).
    const owner = this.stack[base]?.scene;
    if (owner?.sides) owner.sides(w.left, w.right);
    else drawSkyPanels(w.left, w.right, w.stars, o.deepStars, o.reduceFlashing);
    w.all.image(w.left.canvas, 0, 0);
    w.all.image(this.screen.canvas, PANEL_W, 0);
    w.all.image(w.right.canvas, PANEL_W + SCREEN_W, 0);
    this.inset = PANEL_W;
    this.display.present(w.all.canvas, o.crt, o.scale, false);
  }
}
